import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { z } from 'zod'

import { resolveIssueDisplay } from '~/composables/resolveIssueDisplay'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useServerProblemSync } from '~/composables/useServerProblemSync'
import { useTanstackForm } from '~/composables/useTanstackForm'
import { PreferredContactMethod } from '~/enums'
import { MenuSupport } from '~/services/api/contracts/requests/MenuSupport'
import type { MenuSupportRequest } from '~/services/api/generated'
import { PreferredContactMethodSchema } from '~/services/api/generated'
import type { SupportTopic } from '~/services/api/support/types'
import type { FormFieldMap } from '~/services/forms/FormFieldMap'
import type { FormProblemState } from '~/services/forms/FormProblemState'
import { useMenuSupportStore } from '~/stores/useMenuSupportStore'

interface MenuSupportFormValues {
  topic: string
  name: string
  email: string
  phone: string
  preferred_contact_method: PreferredContactMethod
  subject: string
  message: string
}

const defaultMenuSupportFormValues: MenuSupportFormValues = {
  email: '',
  message: '',
  name: '',
  phone: '',
  preferred_contact_method: PreferredContactMethod.EMAIL,
  subject: '',
  topic: ''
}

const menuSupportFieldMap: FormFieldMap<keyof MenuSupportFormValues> = [
  { ui: 'topic', wire: 'topic' },
  { ui: 'name', wire: 'name' },
  { ui: 'email', wire: 'email' },
  { ui: 'phone', wire: 'phone' },
  { ui: 'preferred_contact_method', wire: 'preferred_contact_method' },
  { ui: 'subject', wire: 'subject' },
  { ui: 'message', wire: 'message' }
]

interface UseMenuSupportFormReturn {
  email: Ref<string>
  getFieldError: (field: keyof MenuSupportFormValues) => string
  handleSubmit: () => void
  isSubmitting: ComputedRef<boolean>
  message: Ref<string>
  name: Ref<string>
  phone: Ref<string>
  preferredContactMethod: ComputedRef<PreferredContactMethod>
  problem: ComputedRef<FormProblemState | null>
  selectEmailContact: () => void
  selectPhoneContact: () => void
  selectTopic: (value: string | undefined) => void
  subject: Ref<string>
  submitSuccess: ComputedRef<boolean>
  topic: ComputedRef<SupportTopic | ''>
  topics: ComputedRef<{ value: SupportTopic; label: string }[]>
}

/** The menu support form: inputs bind to the menu support store; submit validates a snapshot. */
export function useMenuSupportForm(
  onSubmit: (request: MenuSupportRequest) => void
): UseMenuSupportFormReturn {
  const store = useMenuSupportStore()
  const { email, message, name, phone, subject } = storeToRefs(store)
  const t = usePresentationTranslator()

  const topics = computed<{ value: SupportTopic; label: string }[]>(() => [
    { label: t('common.menuSupport.topics.buildMyMenu'), value: 'build_my_menu' },
    { label: t('common.menuSupport.topics.availability'), value: 'availability' },
    { label: t('common.menuSupport.topics.pricing'), value: 'pricing' },
    { label: t('common.menuSupport.topics.modifiers'), value: 'modifiers' },
    { label: t('common.menuSupport.topics.technicalIssue'), value: 'technical_issue' }
  ])

  const menuSupportFormSchema = z
    .object({
      email: z.string(),
      message: z.string(),
      name: z.string(),
      phone: z.string(),
      preferred_contact_method: PreferredContactMethodSchema,
      subject: z.string(),
      topic: z.string()
    })
    .transform((values) => {
      const { phone, ...rest } = values
      return {
        ...rest,
        phone: phone === '' ? undefined : phone
      }
    })
    .transform((request, context) => {
      const validation = MenuSupport({}).safeParse(request)
      if (validation.success) return validation.data

      validation.error.issues.forEach((issue) => {
        context.addIssue({
          code: 'custom',
          message: resolveIssueDisplay(issue, t),
          path: issue.path
        })
      })
      return z.NEVER
    })

  const {
    fields,
    getFieldError,
    setFieldServerErrors,
    problem,
    setProblem,
    handleSubmit: submitFormValues
  } = useTanstackForm<MenuSupportFormValues>({
    defaultValues: defaultMenuSupportFormValues,
    formId: 'menu_support',
    onSubmit: async (value) => {
      onSubmit(menuSupportFormSchema.parse(value))
    },
    schema: menuSupportFormSchema
  })

  useServerProblemSync({
    failureMessage: () => t('common.menuSupport.genericSubmitError'),
    fieldMap: menuSupportFieldMap,
    problem: () => store.submitProblem,
    setFieldServerErrors,
    setProblem
  })

  /** Selects the listed topic carrying `value`; any other value is ignored. */
  const selectTopic = (value: string | undefined) => {
    const topic = topics.value.find((item) => item.value === value)?.value
    if (topic) store.selectTopic(topic)
  }

  const selectEmailContact = () => {
    store.preferredContactMethod = PreferredContactMethod.EMAIL
  }

  const selectPhoneContact = () => {
    store.preferredContactMethod = PreferredContactMethod.PHONE
  }

  /** Snapshots the store into the form fields, then validates and submits them. */
  const handleSubmit = () => {
    fields.topic.api.handleChange(store.topic)
    fields.name.api.handleChange(store.name)
    fields.email.api.handleChange(store.email)
    fields.phone.api.handleChange(store.phone)
    fields.preferred_contact_method.api.handleChange(store.preferredContactMethod)
    fields.subject.api.handleChange(store.subject)
    fields.message.api.handleChange(store.message)

    void submitFormValues()
  }

  return {
    email,
    getFieldError,
    handleSubmit,
    isSubmitting: computed(() => store.isSubmitting),
    message,
    name,
    phone,
    preferredContactMethod: computed(() => store.preferredContactMethod),
    problem,
    selectEmailContact,
    selectPhoneContact,
    selectTopic,
    subject,
    submitSuccess: computed(() => store.submitSuccess),
    topic: computed(() => store.topic),
    topics
  }
}
